import { withErrorBoundary } from "../../../../shared-components/ErrorBoundary";
import "../HelpContent.css";
import { Icon } from "../../WorkflowsScope";

const MCPConfigurationC: React.FC<any> = (props: any) => {
  return (
    <>
      <div>
        <div style={{ marginBottom: "18px" }}>
          <h3>MCP Configuration</h3>
        </div>

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Summary:</span>
          <span className="help-content">
            MCP (Model Context Protocol) Configuration enables you to connect your Insight workflows to Nebula MCP. 
            Once configured, Copilot can leverage your workflow's insights to provide intelligent recommendations and responses.
          </span>
        </div>
        <hr />

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>When to Use:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Insight Workflows Only:</b> MCP Configuration is only available for workflows with Type = "Insight". 
                Recommendation workflows cannot be connected to MCP.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Published Workflows:</b> Your workflow should be published and functional before connecting to MCP.
              </span>
            </li>
          </ul>
        </div>
        <hr />

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Configuration Fields:</span>
          
          <div style={{ marginBottom: "10px" }}>
            <Icon name="info" className="help-icon" />
            <span className="help-heading">Insight Type: </span>
            <span className="help-content">
              Automatically populated from your workflow name (with spaces removed). This serves as the unique identifier 
              for your MCP configuration. Must contain only alphanumeric characters.
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="list" className="help-icon" />
            <span className="help-heading">Keywords: </span>
            <span className="help-content">
              Define 1-20 comma-separated keywords that trigger this insight in Copilot conversations. 
              Choose terms that users might naturally use when seeking this type of insight (e.g., "sales opportunity", "customer risk", "usage trend").
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="calculator" className="help-icon" />
            <span className="help-heading">Default Top Count: </span>
            <span className="help-content">
              Specify the default number of results to return (1-10). This controls how many insights Copilot will retrieve 
              when this configuration is triggered.
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="tag" className="help-icon" />
            <span className="help-heading">Field Synonyms: </span>
            <span className="help-content">
              Map up to 20 natural language terms to your data fields. Each synonym helps Copilot understand different ways 
              users might reference the same concept. Format: <code>natural term = actual_field_name</code>
              <br /><br />
              <b>Example:</b> If your data has a field named <code>AccountRevenue</code>, add synonyms like:
              <br />
              • "revenue = AccountRevenue"
              <br />
              • "sales = AccountRevenue"
              <br />
              • "income = AccountRevenue"
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="lightbulb" className="help-icon" />
            <span className="help-heading">Golden Prompts: </span>
            <span className="help-content">
              Define 1-10 example prompts for testing and demonstrating this insight. These prompts help validate that your 
              configuration works correctly and serve as examples for users. Enter prompts separated by commas or on separate lines.
              <br /><br />
              <b>Example:</b>
              <br />
              • "Show me migration insights for &lt;CustomerName&gt;"
              <br />
              • "What are the migration insights for &lt;TPID&gt;?"
              <br />
              • "Display migration propensity for this account"
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="document" className="help-icon" />
            <span className="help-heading">Template: </span>
            <span className="help-content">
              Define how Copilot should format and present the insights (max 5000 characters). Use placeholders that match 
              your workflow's output fields. Supports Markdown for rich formatting.
              <br /><br />
              <b>Example:</b>
              <br />
              <code>
                ### Top Opportunities<br />
                **Account:** {'{{AccountName}}'}<br />
                **Revenue Potential:** ${'{{AccountRevenue}}'}<br />
                **Risk Score:** {'{{RiskScore}}'}/100
              </code>
            </span>
          </div>
        </div>
        <hr />

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Actions:</span>
          
          <div style={{ marginBottom: "10px" }}>
            <Icon name="save" className="help-icon" />
            <span className="help-heading">Connect MCP: </span>
            <span className="help-content">
              Saves your configuration to the UAT environment. The system automatically detects if this is a new configuration 
              (create) or an update to an existing one based on the Insight Type.
            </span>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <Icon name="cloud" className="help-icon" />
            <span className="help-heading">Deploy to Prod: </span>
            <span className="help-content">
              Deploys the same configuration from UAT to the Production environment. This button appears after successfully 
              saving to UAT. The system checks if a production configuration exists and automatically performs create or update 
              accordingly.
            </span>
          </div>
        </div>
        <hr />

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Validation Rules:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Required Fields:</b> Insight Type, Keywords, Default Top Count, Golden Prompts, 
                and Template must all be provided.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Insight Type:</b> Must be alphanumeric (no spaces or special characters).
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Keywords:</b> Minimum 1, maximum 20 comma-separated values.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Default Top Count:</b> Must be between 1 and 10.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Field Synonyms:</b> Maximum 20 synonyms total. Format must be "term = field_name" 
                with one synonym per line.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Golden Prompts:</b> Minimum 1, maximum 10 prompts. Can be separated by commas 
                or entered on separate lines.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Template:</b> Maximum 5000 characters.
              </span>
            </li>
          </ul>
        </div>
        <hr />

        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Tips for Success:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Keyword Selection:</b> Choose keywords that are specific enough to avoid 
                ambiguity but broad enough to catch natural language variations. Think about how users would ask for this insight 
                in a conversation.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Field Synonyms:</b> Include both formal terms and common abbreviations. 
                Consider regional variations and industry jargon your users might employ.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Template Design:</b> Keep templates concise and scannable. Use Markdown formatting 
                for emphasis and structure. Test with sample data to ensure placeholders match your workflow output.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                <b style={{ fontWeight: "500" }}>Iterative Testing:</b> Start with a minimal configuration in UAT, test with Copilot, 
                refine based on results, then deploy to production.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

const MCPConfiguration = withErrorBoundary("MCP Configuration Help", MCPConfigurationC);
export default MCPConfiguration;
